// src/pages/publicacoes/HomologacaoDetalhe.tsx
// UPDATED: 2026-07-08 — v2.0
// ✅ documentUrl adicionado ao tipo e à renderização
// ✅ Botões Visualizar + Baixar para PDF/DOCX
// ✅ iframe inline para PDF

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, FileText, Calendar,
  Download, Loader2, AlertTriangle, Hash, Eye,
} from 'lucide-react';
import { API_BASE } from '../utils/apiConfig';

interface Homologacao {
  id:              string;
  title:           string;
  description:     string | null;
  content:         string | null;
  type:            string;
  referenceNumber: string | null;
  licenseNumber:   string | null;
  coverImageUrl:   string | null;
  documentUrl:     string | null;   // ← ADICIONADO
  issuedAt:        string | null;
  publishedAt:     string | null;
  viewCount:       number;
  tags:            string[] | null;
}

const TYPE_LABELS: Record<string, string> = {
  course_approval:   'Aprovação de Curso',
  license:           'Licença',
  certification:     'Certificação',
  official_document: 'Documento Oficial',
  credential:        'Credencial',
  regulation:        'Regulamento',
  other:             'Outros',
};

const fmt = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }) : null;

const isPdf = (url: string) => url.toLowerCase().includes('.pdf');

const HomologacaoDetalhe: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [doc, setDoc]         = useState<Homologacao | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    const fetch_ = async () => {
      setLoading(true); setError(null);
      try {
        const res = await fetch(`${API_BASE}/homologation/${id}`);
        if (!res.ok) throw new Error('Homologação não encontrada');
        const data = await res.json();
        setDoc(data);
      } catch (e: any) {
        setError(e.message);
      } finally { setLoading(false); }
    };
    fetch_();
  }, [id]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="w-10 h-10 text-amber-600 animate-spin"/>
        <p className="text-gray-500 text-sm">Carregando homologação…</p>
      </div>
    </div>
  );

  if (error || !doc) return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6">
      <div className="text-center max-w-md">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4"/>
        <h2 className="text-2xl font-black text-gray-900 mb-2">Não encontrado</h2>
        <p className="text-gray-500 mb-6">{error ?? 'Esta homologação não existe ou foi removida.'}</p>
        <Link to="/publicacoes/homologacoes"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-gray-900 text-white font-bold hover:shadow-lg transition-all">
          <ArrowLeft className="w-4 h-4"/> Voltar à lista
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">

      {/* ── IMAGEM HERO ─────────────────────────────────────────────────────── */}
      {doc.coverImageUrl ? (
        <div className="relative w-full bg-gray-900 overflow-hidden" style={{ height: 'min(55vw, 480px)' }}>
          <img
            src={doc.coverImageUrl}
            alt={doc.title}
            className="w-full h-full object-cover opacity-90"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/10 to-transparent"/>
          <a
            href={doc.coverImageUrl}
            download
            className="absolute top-4 right-4 md:top-6 md:right-6 flex items-center gap-2 px-4 py-2 rounded-xl bg-black/60 text-white text-sm font-bold backdrop-blur-sm hover:bg-black/80 transition-all"
          >
            <Download className="w-4 h-4"/> Baixar imagem
          </a>
          <button
            onClick={() => navigate('/publicacoes/homologacoes')}
            className="absolute top-4 left-4 md:top-6 md:left-6 flex items-center gap-2 px-4 py-2 rounded-xl bg-black/60 text-white text-sm font-bold backdrop-blur-sm hover:bg-black/80 transition-all"
          >
            <ArrowLeft className="w-4 h-4"/> Voltar
          </button>
        </div>
      ) : (
        <div className="w-full bg-gradient-to-br from-gray-950 via-amber-950 to-gray-950 pt-16 pb-10 px-6 relative">
          <button
            onClick={() => navigate('/publicacoes/homologacoes')}
            className="absolute top-4 left-4 md:top-6 md:left-6 flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 text-white text-sm font-bold hover:bg-white/20 transition-all"
          >
            <ArrowLeft className="w-4 h-4"/> Voltar
          </button>
          <div className="max-w-3xl mx-auto text-center">
            <FileText className="w-14 h-14 text-amber-400 mx-auto mb-4"/>
          </div>
        </div>
      )}

      {/* ── CONTEÚDO ────────────────────────────────────────────────────────── */}
      <div className="max-w-3xl mx-auto px-5 md:px-8 pb-20">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-gray-400 mt-6 mb-8">
          <Link to="/" className="hover:text-amber-600 transition-colors">Início</Link>
          <span>/</span>
          <Link to="/publicacoes/homologacoes" className="hover:text-amber-600 transition-colors">Homologações</Link>
          <span>/</span>
          <span className="text-gray-600 truncate max-w-[200px]">{doc.title}</span>
        </nav>

        <span className="inline-block px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold mb-4">
          {TYPE_LABELS[doc.type] ?? doc.type}
        </span>

        <h1 className="text-3xl md:text-4xl font-black text-gray-900 leading-tight mb-6">
          {doc.title}
        </h1>

        {/* Metadados */}
        <div className="flex flex-wrap gap-4 mb-8 pb-6 border-b border-gray-100">
          {doc.licenseNumber && (
            <div className="flex items-center gap-2 text-sm">
              <Hash className="w-4 h-4 text-amber-500 flex-shrink-0"/>
              <span className="text-gray-500">Licença FEPCSSP:</span>
              <strong className="text-gray-900">{doc.licenseNumber}</strong>
            </div>
          )}
          {doc.referenceNumber && (
            <div className="flex items-center gap-2 text-sm">
              <Hash className="w-4 h-4 text-amber-500 flex-shrink-0"/>
              <span className="text-gray-500">Homologação FEPCSSP:</span>
              <strong className="text-gray-900">{doc.referenceNumber}</strong>
            </div>
          )}
          {doc.issuedAt && (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Calendar className="w-4 h-4 flex-shrink-0"/>
              <span>Emitido em {fmt(doc.issuedAt)}</span>
            </div>
          )}
        </div>

        {/* Resumo */}
        {doc.description && (
          <p className="text-xl text-gray-600 leading-relaxed font-medium border-l-4 border-amber-400 pl-5 mb-10">
            {doc.description}
          </p>
        )}

        {/* Conteúdo completo */}
        {doc.content ? (
          <div
            className="text-gray-800 leading-[1.9] text-[16px] whitespace-pre-wrap mb-10"
            style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
          >
            {doc.content}
          </div>
        ) : (
          <div className="py-12 text-center border border-dashed border-gray-200 rounded-2xl text-gray-400 text-sm mb-10">
            Conteúdo completo não disponível.
          </div>
        )}

        {/* ── DOCUMENTO ANEXADO ─────────────────────────────────────────────── */}
        {doc.documentUrl && (
          <div className="mb-10 space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
              <FileText className="w-4 h-4 text-amber-500"/>
              <p className="text-xs font-black text-gray-400 uppercase tracking-wider">Documento Anexado</p>
            </div>

            <div className="flex gap-3 flex-wrap">
              <a
                href={doc.documentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-3 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 transition-colors shadow-sm"
              >
                <Eye className="w-4 h-4"/> Visualizar documento
              </a>
              <a
                href={doc.documentUrl}
                download
                className="flex items-center gap-2 px-5 py-3 rounded-xl border-2 border-gray-200 text-gray-700 text-sm font-bold hover:bg-gray-50 transition-colors"
              >
                <Download className="w-4 h-4"/> Baixar
              </a>
            </div>

            {/* PDF inline */}
            {isPdf(doc.documentUrl) && (
              <iframe
                src={doc.documentUrl}
                className="w-full rounded-2xl border border-gray-200 shadow-sm"
                style={{ height: '600px' }}
                title={doc.title}
              />
            )}

            {/* DOCX — aviso */}
            {!isPdf(doc.documentUrl) && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <FileText className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"/>
                <p className="text-sm text-amber-800">
                  Arquivos Word (.docx) não podem ser visualizados diretamente no navegador.
                  Use o botão <strong>Baixar</strong> para abrir no seu computador.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Tags */}
        {doc.tags && doc.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4 pt-8 border-t border-gray-100">
            {doc.tags.map(tag => (
              <span key={tag} className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Rodapé de ações */}
        <div className="mt-12 pt-8 border-t border-gray-100 flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => navigate('/publicacoes/homologacoes')}
            className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-2xl border border-gray-200 font-bold text-gray-700 hover:bg-gray-50 transition-all text-sm"
          >
            <ArrowLeft className="w-4 h-4"/> Ver todas as homologações
          </button>
          <Link to="/contato"
            className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-2xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 text-white hover:shadow-lg transition-all text-sm"
          >
            Falar com a Federação <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>

        <p className="mt-8 text-xs text-center text-gray-400">
          Federação dos Profissionais da Beleza — Para confirmar alguma informação, entre em contato.
          {doc.publishedAt && <> · Publicado em {fmt(doc.publishedAt)}</>}
        </p>
      </div>
    </div>
  );
};

export default HomologacaoDetalhe;